// Console navigation: grouped sections, as in the enforza-neon side nav.
// Drives the sidebar, the routes and the breadcrumb labels.
import { BarChart3, Bell, Boxes, ClipboardList, Code2, FilePlus2, FileText, Globe, CreditCard, Inbox, LayoutDashboard, Mail, Paintbrush, PoundSterling, Settings2, Sparkles, SquareStack, UserRound, Users, type LucideIcon } from "lucide-react";

export interface NavItem {
  path: string; // route path without the leading slash
  label: string;
  icon: LucideIcon;
  blurb: string; // one-line description for placeholder pages
  soon?: boolean; // shown greyed out with a "Soon" chip, not clickable
  custom?: boolean; // has its own page (not the generic placeholder)
  /** Highlight the row for these paths too (default: its own path only) */
  activeFor?: (pathname: string) => boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    label: "Overview",
    items: [
      { path: "dashboard", label: "Dashboard", icon: LayoutDashboard, blurb: "The last 30 days across all your website components.", custom: true },
      { path: "analytics", label: "Analytics", icon: BarChart3, blurb: "Loads, searches, prices and enquiries by period and component.", custom: true },
    ],
  },
  {
    label: "Build",
    items: [
      // Straight into the new-component wizard
      { path: "components/new", label: "New component", icon: Sparkles, blurb: "Create a calculator or page for your website.", custom: true },
      // The website component cards; stays highlighted inside one (its Branding, Pricing… tabs)
      {
        path: "components",
        label: "Website components",
        icon: SquareStack,
        blurb: "Customise the calculators and pages that win you leads.",
        custom: true,
        activeFor: (p) => /^\/components(\/|$)/.test(p) && !p.startsWith("/components/new"),
      },
    ],
  },
  {
    // No lead inbox: enquiries are emailed to the installer and never stored.
    // Estimator Leads lists the homes that got an estimate (addresses only, no
    // names). Above Quotes: leads come first, quotes follow from them.
    label: "Leads",
    items: [
      { path: "leads", label: "Estimator Leads", icon: Inbox, blurb: "Estimates, callbacks, instant quotes and contact form enquiries.", custom: true },
      { path: "mailshots", label: "Mailshots", icon: Mail, blurb: "Send a printed mailshot to captured addresses to warm them up." },
    ],
  },
  {
    // Proper quotes (docs/10-quote-engine.md): built from the company's catalogue
    // and prices, sized from the home's heat loss, sent to the customer to accept
    label: "Quotes",
    items: [
      { path: "quotes/new", label: "New quote", icon: FilePlus2, blurb: "Quote a home from its postcode.", custom: true },
      {
        path: "quotes",
        label: "Quotes",
        icon: FileText,
        blurb: "Every quote you've built, sent and won.",
        custom: true,
        // Stays highlighted inside a quote (but not on the other Quotes pages)
        activeFor: (p) => /^\/quotes(\/|$)/.test(p) && !/^\/quotes\/(new|catalogue|settings)/.test(p),
      },
      { path: "quotes/catalogue", label: "Catalogue", icon: Boxes, blurb: "Your products and cost prices.", custom: true },
      { path: "quotes/settings", label: "Quote settings", icon: Settings2, blurb: "Margins, packages, extras, running costs and how quotes look.", custom: true },
    ],
  },
  {
    label: "Account",
    items: [
      { path: "profile", label: "Profile", icon: UserRound, blurb: "Your company's details.", custom: true },
      { path: "users", label: "Users", icon: Users, blurb: "Who can sign in: invite people, set roles.", custom: true },
      { path: "billing", label: "Billing", icon: CreditCard, blurb: "Subscriptions and invoices.", custom: true },
    ],
  },
];

/** Every nav item, flattened (for routes and breadcrumb labels). */
export const navItems: NavItem[] = navSections.flatMap((s) => s.items);

/**
 * Tabs inside a single tool (/components/:widgetId/<tab>). Flags limit a tab to
 * some components (no flag = every component):
 *   estimateOnly  not contact forms (they have no prices)
 *   contactOnly   contact forms only
 *   hostedOnly    hosted pages only
 *   ashpOnly      heat pump components only (product "ashp", which includes contact forms)
 *   solarOnly     solar estimates only (product "solar", docs/12-solar-roof-scan.md §9)
 * A solar estimate shows Branding, Pricing (its own price editor, routed by
 * PricingTab), Notifications, Hosted page (if hosted) and Install. Heat pump-only
 * screens inside a shared tab (the BUS grant, kW sizes, Instant quotes) live in
 * the heat pump PricingPage, which a solar estimate never opens.
 */
export const widgetTabs = [
  { path: "branding", label: "Branding", icon: Paintbrush },
  // Estimates only: a contact form has no prices (solar: system and inverter prices; heat pumps: kW sizes)
  { path: "pricing", label: "Pricing", icon: PoundSterling, estimateOnly: true },
  // Contact forms only: the address look-up and the topic drop-down
  { path: "form", label: "Contact form", icon: ClipboardList, contactOnly: true, ashpOnly: true },
  { path: "notifications", label: "Notifications", icon: Bell },
  // Hosted widgets only: the page's header/footer, link and QR code
  { path: "hosted", label: "Hosted page", icon: Globe, hostedOnly: true },
  { path: "install", label: "Install", icon: Code2 },
] as const;

export type WidgetTab = (typeof widgetTabs)[number];

/** Whether a tab shows for a component of this style ("small", "full"…) and product. */
export function tabVisible(tab: WidgetTab, style: string, product: "ashp" | "solar"): boolean {
  if ("solarOnly" in tab && tab.solarOnly && product !== "solar") return false;
  if ("ashpOnly" in tab && tab.ashpOnly && product !== "ashp") return false;
  if ("hostedOnly" in tab) return style === "hosted";
  if ("contactOnly" in tab) return style === "contact";
  if ("estimateOnly" in tab) return style !== "contact";
  return true;
}
